import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Download, Save, Loader2, ChevronRight, Clock,
  Mail, FileText, Image, MessageSquare, ClipboardList, FileSpreadsheet, PenLine, CheckCircle2,
  Table2, Search, BookOpen, AlertTriangle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PricingBadge } from '@/components/opportunities/OppBadges';
import { Loader } from '@/components/common/Feedback';
import api from '@/lib/api';
import { fmtMoney, fmtAmount, fmtDate, fmtRelative, SOURCE_LABEL, CO_STATUSES, CURRENCY_META } from '@/lib/format';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const SRC_ICON = {
  email: Mail, drawing: PenLine, rfi: FileText, photo: Image,
  meeting_notes: ClipboardList, whatsapp: MessageSquare, daily_report: FileSpreadsheet, timesheet: Clock,
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function ChangeOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => api.get(`/change-orders/${id}`).then((r) => setCo(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  // ---- live totals ----
  const totals = useMemo(() => {
    if (!co) return {};
    const labor = (co.labor_lines || []).reduce((s, l) => s + round2((l.hours || 0) * (l.rate || 0)), 0);
    const mat = (co.material_lines || []).reduce((s, m) => s + round2((m.quantity || 0) * (m.unit_price || 0)), 0);
    const equip = (co.equipment_lines || []).reduce((s, e) => s + round2((e.quantity || 0) * (e.unit_price || 0)), 0);
    const subtotal = round2(labor + mat + equip);
    const markup = round2(subtotal * (Number(co.markup_pct) || 0) / 100);
    return { labor: round2(labor), mat: round2(mat), equip: round2(equip), subtotal, markup, total: round2(subtotal + markup) };
  }, [co]);

  // Deterministic shared calculation (Phase 12). When present it is the single
  // source of truth for the money — the legacy line/markup editor is superseded.
  const det = useMemo(() => {
    const c = co?.calculation;
    if (c && ((c.lines || []).length || c.pricing_status)) return c;
    return null;
  }, [co]);

  const setField = (key, val) => { setCo((c) => ({ ...c, [key]: val })); setDirty(true); };
  const setLine = (listKey, idx, field, val) => {
    setCo((c) => {
      const list = [...(c[listKey] || [])];
      list[idx] = { ...list[idx], [field]: field === 'craft' || field === 'item' || field === 'unit' ? val : (val === '' ? '' : Number(val)) };
      return { ...c, [listKey]: list };
    });
    setDirty(true);
  };
  const addLine = (listKey) => {
    const blank = listKey === 'labor_lines' ? { craft: '', hours: 0, rate: 0 }
      : listKey === 'material_lines' ? { item: '', unit: 'EA', quantity: 0, unit_price: 0 }
      : { item: '', quantity: 0, unit_price: 0 };
    setCo((c) => ({ ...c, [listKey]: [...(c[listKey] || []), blank] }));
    setDirty(true);
  };
  const removeLine = (listKey, idx) => {
    setCo((c) => ({ ...c, [listKey]: c[listKey].filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  // Rate-book calculator: insert a material line from a project Rate Card item (qty x rate).
  const addMaterialFromRate = (rate, qty) => {
    const q = Number(qty) || 1;
    setCo((c) => ({
      ...c,
      material_lines: [
        ...(c.material_lines || []),
        { item: rate.service, unit: rate.unit || 'EA', quantity: q, unit_price: Number(rate.rate) || 0 },
      ],
    }));
    setDirty(true);
    toast.success(`Added "${rate.service}" from the project Rate Card`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: co.title, description: co.description, justification: co.justification,
        labor_lines: co.labor_lines, material_lines: co.material_lines, equipment_lines: co.equipment_lines,
        markup_pct: Number(co.markup_pct) || 0, time_extension_days: Number(co.time_extension_days) || 0,
        clause_refs: co.clause_refs,
      };
      const r = await api.patch(`/change-orders/${id}`, payload);
      setCo(r.data); setDirty(false);
      toast.success('Change order saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const changeStatus = async (status) => {
    try {
      await api.post(`/change-orders/${id}/status`, { status });
      toast.success(`Moved to ${status}`);
      load();
    } catch (e) { toast.error('Failed to update status'); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.post(`/change-orders/${id}/export`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${co.co_number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch (e) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (loading) return <Loader />;
  if (!co) return null;
  const totalColor = co.status === 'Paid' || co.status === 'Approved' || co.status === 'Invoiced' ? 'text-money-700'
    : co.status === 'Negotiation' ? 'text-danger-700' : 'text-warn-700';

  return (
    <div className="space-y-5 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate('/change-orders')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-cos">
          <ArrowLeft className="h-4 w-4" /> Change Orders
        </button>
        <div className="flex items-center gap-2">
          {dirty && <Button onClick={save} disabled={saving} className="bg-primary hover:bg-ink-700 h-9" data-testid="save-co-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save changes
          </Button>}
          <Button onClick={exportPdf} disabled={exporting} variant="secondary" className="h-9 border border-slate-300" data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Export PDF
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-mono font-semibold text-slate-700">{co.co_number}</span>
            <ChevronRight className="h-3 w-3" />
            <span>{co.project?.name}</span>
          </div>
          <input value={co.title || ''} onChange={(e) => setField('title', e.target.value)} data-testid="co-title-input"
            className="mt-1 text-xl font-semibold text-slate-900 bg-transparent border-0 border-b border-transparent hover:border-slate-200 focus:border-slate-400 focus:outline-none w-full max-w-2xl" />
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={co.status} testid="co-status-badge" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="h-8 border border-slate-300" data-testid="status-change-btn">Advance status</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {CO_STATUSES.map((s) => (
                <DropdownMenuItem key={s} onClick={() => changeStatus(s)} data-testid={`status-opt-${s}`} disabled={s === co.status}>
                  <CheckCircle2 className={cn('h-3.5 w-3.5 mr-2', s === co.status ? 'text-money-600' : 'text-slate-300')} />{s}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {co.is_legacy_pricing && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 flex gap-2" data-testid="co-legacy-banner">
          <AlertTriangle className="h-4 w-4 text-violet-600 shrink-0 mt-0.5" />
          <div className="text-xs text-violet-800">
            <b>Legacy change order.</b> This record was priced with a superseded model and is <b>not</b> a confirmed
            deterministic calculation. Its figures are shown as a legacy estimate only. Re-price it via the opportunity&rsquo;s
            calculator before submitting.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Editor */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="p-5 space-y-4">
            <Field label="Description of Changed Work">
              <Textarea rows={3} value={co.description || ''} onChange={(e) => setField('description', e.target.value)} data-testid="co-description" />
            </Field>
            <Field label="Justification (contractual basis)">
              <Textarea rows={3} value={co.justification || ''} onChange={(e) => setField('justification', e.target.value)} data-testid="co-justification" />
            </Field>
          </Card>

          {/* Labor */}
          <LineTable title="Labor Estimate" testid="labor-table"
            columns={['Craft', 'Hours', 'Rate', 'Amount']}
            rows={co.labor_lines} subtotal={totals.labor}
            onAdd={() => addLine('labor_lines')} onRemove={(i) => removeLine('labor_lines', i)}
            render={(l, i) => ([
              <Input value={l.craft} onChange={(e) => setLine('labor_lines', i, 'craft', e.target.value)} className="h-8" data-testid={`labor-craft-${i}`} />,
              <Input type="number" value={l.hours} onChange={(e) => setLine('labor_lines', i, 'hours', e.target.value)} className="h-8 text-right font-mono" data-testid={`labor-hours-${i}`} />,
              <Input type="number" value={l.rate} onChange={(e) => setLine('labor_lines', i, 'rate', e.target.value)} className="h-8 text-right font-mono" data-testid={`labor-rate-${i}`} />,
              fmtMoney(round2((l.hours || 0) * (l.rate || 0)), 2),
            ])} />

          {/* Rate Book calculator — pick project rates into the estimate */}
          <RateBookPicker
            rates={co.project?.rate_table || []}
            currency={co.project?.rate_table_currency || co.project?.currency || 'USD'}
            projectId={co.project?.id}
            onAdd={addMaterialFromRate}
          />

          {/* Material */}
          <LineTable title="Material Estimate" testid="material-table"
            columns={['Item', 'Unit', 'Qty', 'Unit Price', 'Amount']}
            rows={co.material_lines} subtotal={totals.mat}
            onAdd={() => addLine('material_lines')} onRemove={(i) => removeLine('material_lines', i)}
            render={(m, i) => ([
              <Input value={m.item} onChange={(e) => setLine('material_lines', i, 'item', e.target.value)} className="h-8" data-testid={`mat-item-${i}`} />,
              <Input value={m.unit} onChange={(e) => setLine('material_lines', i, 'unit', e.target.value)} className="h-8 w-16" data-testid={`mat-unit-${i}`} />,
              <Input type="number" value={m.quantity} onChange={(e) => setLine('material_lines', i, 'quantity', e.target.value)} className="h-8 text-right font-mono" data-testid={`mat-qty-${i}`} />,
              <Input type="number" value={m.unit_price} onChange={(e) => setLine('material_lines', i, 'unit_price', e.target.value)} className="h-8 text-right font-mono" data-testid={`mat-price-${i}`} />,
              fmtMoney(round2((m.quantity || 0) * (m.unit_price || 0)), 2),
            ])} />

          {(co.equipment_lines || []).length > 0 && (
            <LineTable title="Equipment" testid="equipment-table"
              columns={['Item', 'Qty', 'Unit Price', 'Amount']}
              rows={co.equipment_lines} subtotal={totals.equip}
              onAdd={() => addLine('equipment_lines')} onRemove={(i) => removeLine('equipment_lines', i)}
              render={(e2, i) => ([
                <Input value={e2.item} onChange={(e) => setLine('equipment_lines', i, 'item', e.target.value)} className="h-8" />,
                <Input type="number" value={e2.quantity} onChange={(e) => setLine('equipment_lines', i, 'quantity', e.target.value)} className="h-8 text-right font-mono" />,
                <Input type="number" value={e2.unit_price} onChange={(e) => setLine('equipment_lines', i, 'unit_price', e.target.value)} className="h-8 text-right font-mono" />,
                fmtMoney(round2((e2.quantity || 0) * (e2.unit_price || 0)), 2),
              ])} />
          )}

          {/* Schedule impact */}
          <Card className="p-5">
            <div className="text-sm font-semibold text-slate-800 mb-3">Schedule Impact</div>
            <ScheduleTimeline impact={co.schedule_impact} extDays={co.time_extension_days} />
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-500">Requested time extension</span>
              <Input type="number" value={co.time_extension_days || 0} onChange={(e) => setField('time_extension_days', Number(e.target.value))}
                className="h-8 w-20 text-right font-mono" data-testid="time-extension-input" />
              <span className="text-xs text-slate-500">calendar days</span>
            </div>
          </Card>

          {/* Clause refs */}
          <Card className="p-5">
            <div className="text-sm font-semibold text-slate-800 mb-2">Contractual References</div>
            <div className="flex flex-wrap gap-2">
              {(co.clause_refs || []).map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700">
                  {c}
                  <button onClick={() => setField('clause_refs', co.clause_refs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
              <AddClause onAdd={(v) => setField('clause_refs', [...(co.clause_refs || []), v])} />
            </div>
          </Card>
        </div>

        {/* Sidebar: totals + evidence */}
        <div className="space-y-5">
          <Card className="p-5 lg:sticky lg:top-20">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-800">Cost Summary</div>
              {det && <PricingBadge status={det.pricing_status || 'unquantified'} testid="co-detail-pricing" />}
            </div>
            {det ? (
              <>
                {det.pricing_status !== 'fully_priced' && (
                  <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-800" data-testid="co-detail-incomplete">
                    Labelled <b>{det.pricing_status === 'partially_priced' ? 'Partially priced' : 'Unquantified'}</b> — incomplete items are &ldquo;financial impact to be substantiated&rdquo;. No partial figure is shown as the final total.
                  </div>
                )}
                <Row label="Base subtotal" value={det.breakdown?.base_subtotal} currency={det.currency} />
                {det.tender_adjustment?.applied && <Row label="Tender adjustment (base-rate lines)" value={det.breakdown?.tender_amount} currency={det.currency} />}
                <Row label="Markups" value={det.breakdown?.markup_total} currency={det.currency} />
                <div className="flex items-center justify-between border-t-2 border-slate-900 mt-2 pt-3">
                  <span className="text-sm font-semibold text-slate-800">Total Requested</span>
                  {det.pricing_status === 'fully_priced'
                    ? <span className="text-2xl font-semibold font-mono text-money-700" data-testid="co-total">{fmtAmount(det.final_amount, det.currency)}</span>
                    : <span className="text-sm font-semibold text-amber-600" data-testid="co-total">To be substantiated</span>}
                </div>
                <div className="mt-1 text-xs text-slate-400">+{co.time_extension_days || 0} days time extension</div>
                <div className="mt-3 text-[11px] text-slate-400">Money is set by the shared deterministic calculation on the opportunity — the same numbers as the notice. Edit pricing in the opportunity's calculator.</div>
              </>
            ) : (
              <>
                <Row label="Labor subtotal" value={totals.labor} />
                <Row label="Material subtotal" value={totals.mat} />
                {totals.equip > 0 && <Row label="Equipment" value={totals.equip} />}
                <div className="flex items-center justify-between py-1.5 text-sm border-t border-slate-100 mt-1">
                  <span className="text-slate-500">Markup</span>
                  <div className="flex items-center gap-1">
                    <Input type="number" value={co.markup_pct || 0} onChange={(e) => setField('markup_pct', Number(e.target.value))}
                      className="h-7 w-16 text-right font-mono text-xs" data-testid="markup-input" />
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                </div>
                <Row label="Markup amount" value={totals.markup} />
                <div className="flex items-center justify-between border-t-2 border-slate-900 mt-2 pt-3">
                  <span className="text-sm font-semibold text-slate-800">{co.is_legacy_pricing ? 'Legacy estimate' : 'Total Requested'}</span>
                  <span className={cn('text-2xl font-semibold font-mono', totalColor)} data-testid="co-total">{fmtMoney(totals.total)}</span>
                </div>
                {co.is_legacy_pricing && <div className="mt-1 text-[11px] text-violet-600">Superseded pricing model — not a deterministic total.</div>}
                <div className="mt-1 text-xs text-slate-400">+{co.time_extension_days || 0} days time extension</div>
                {dirty && <div className="mt-3 text-xs text-warn-700 bg-amber-50 rounded-md px-2 py-1.5">Unsaved edits &mdash; totals recalculated live</div>}
              </>
            )}
          </Card>

          <Card className="p-5">
            <div className="text-sm font-semibold text-slate-800 mb-3">Evidence Trail ({(co.evidence || []).length})</div>
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {(co.evidence || []).map((e) => {
                const Icon = SRC_ICON[e.source_type] || FileText;
                return (
                  <div key={e.id} className="rounded-md border border-slate-200 p-3" data-testid={`evidence-${e.id}`}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-slate-500" />
                      <span className="text-xs font-semibold text-slate-700">{SOURCE_LABEL[e.source_type] || e.source_type}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{fmtDate(e.timestamp)}</span>
                    </div>
                    <div className="text-xs font-medium text-slate-700 mt-1">{e.subject}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{e.content}</div>
                  </div>
                );
              })}
              {(co.evidence || []).length === 0 && <div className="text-xs text-slate-400">No evidence attached.</div>}
            </div>
          </Card>

          <Card className="p-5">
            <div className="text-sm font-semibold text-slate-800 mb-3">Lifecycle History</div>
            <div className="space-y-3">
              {(co.status_history || []).map((h, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-slate-300" />
                  <div>
                    <div className="text-xs font-medium text-slate-700">{h.status}</div>
                    <div className="text-[10px] text-slate-400">{h.by} &middot; {fmtRelative(h.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>{children}</div>
);

const Row = ({ label, value, currency }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-mono font-medium text-slate-800">{currency ? fmtAmount(value, currency) : fmtMoney(value, 2)}</span>
  </div>
);

const LineTable = ({ title, columns, rows, render, subtotal, onAdd, onRemove, testid }) => (
  <Card className="p-0 overflow-hidden" data-testid={testid}>
    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      <Button size="sm" variant="ghost" onClick={onAdd} className="h-7 text-xs" data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" />Add line</Button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            {columns.map((c, i) => <th key={c} className={cn('px-3 py-2', i === 0 ? 'text-left' : 'text-right')}>{c}</th>)}
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {(rows || []).map((row, i) => {
            const cells = render(row, i);
            return (
              <tr key={i}>
                {cells.map((cell, j) => (
                  <td key={j} className={cn('px-3 py-1.5', j === 0 ? '' : 'text-right', j === cells.length - 1 && 'font-mono font-medium text-slate-800')}>{cell}</td>
                ))}
                <td className="px-2 text-right">
                  <button onClick={() => onRemove(i)} className="text-slate-300 hover:text-red-500" data-testid={`${testid}-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            );
          })}
          {(rows || []).length === 0 && <tr><td colSpan={columns.length + 1} className="px-5 py-4 text-center text-xs text-slate-400">No lines. Add one.</td></tr>}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-200 bg-slate-50">
            <td colSpan={columns.length - 1} className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Subtotal</td>
            <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">{fmtMoney(subtotal, 2)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  </Card>
);

const ScheduleTimeline = ({ impact, extDays }) => {
  const tasks = impact?.tasks || [];
  const maxDays = Math.max(extDays || 1, ...tasks.map((t) => t.days || 0), 1);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <div className="h-2 w-16 rounded bg-slate-300" /><span className="text-slate-500">Baseline schedule</span>
        <div className="h-2 w-10 rounded bg-amber-500 ml-3" /><span className="text-slate-500">Added impact (+{extDays || 0}d)</span>
      </div>
      {tasks.map((t, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-32 text-xs text-slate-600 truncate">{t.name}</div>
          <div className="flex-1 bg-slate-100 rounded h-3 overflow-hidden">
            <div className="h-full bg-amber-500 rounded" style={{ width: `${Math.min(100, ((t.days || 0) / maxDays) * 100)}%` }} />
          </div>
          <div className="w-12 text-right text-xs font-mono text-slate-600">{t.days}d</div>
        </div>
      ))}
    </div>
  );
};

const AddClause = ({ onAdd }) => {
  const [v, setV] = useState('');
  const [editing, setEditing] = useState(false);
  if (!editing) return <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-500 hover:border-slate-400"><Plus className="h-3 w-3" />Add reference</button>;
  return (
    <span className="inline-flex items-center gap-1">
      <Input autoFocus value={v} onChange={(e) => setV(e.target.value)} className="h-7 w-48 text-xs"
        onKeyDown={(e) => { if (e.key === 'Enter' && v.trim()) { onAdd(v.trim()); setV(''); setEditing(false); } }} />
      <Button size="sm" className="h-7" onClick={() => { if (v.trim()) { onAdd(v.trim()); setV(''); } setEditing(false); }}>Add</Button>
    </span>
  );
};


// Rate Book: pick unit rates from the project's Rate Card into the estimate.
// Authored rates are in the PROJECT currency (shown as-is, not FX-converted);
// the numeric value is inserted into the material line unit price.
const RateBookPicker = ({ rates = [], currency = 'USD', projectId, onAdd }) => {
  const [q, setQ] = useState('');
  const [qty, setQty] = useState({});
  const meta = CURRENCY_META[currency] || CURRENCY_META.USD;
  const fmt = (n) => {
    try {
      return new Intl.NumberFormat(meta.locale, { style: 'currency', currency: meta.code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
    } catch { return `${meta.symbol}${Number(n || 0).toFixed(2)}`; }
  };
  const filtered = rates.filter((r) => {
    const s = `${r.service} ${r.category} ${r.unit}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });
  // Group filtered items by category (first-seen order) for easier scanning.
  const groups = [];
  const gIdx = {};
  filtered.forEach((r) => {
    const globalIndex = rates.indexOf(r);
    const cat = (r.category || 'General').trim() || 'General';
    if (gIdx[cat] === undefined) { gIdx[cat] = groups.length; groups.push({ category: cat, items: [] }); }
    groups[gIdx[cat]].items.push({ r, i: globalIndex });
  });

  const ItemRow = ({ r, i }) => (
    <div className="flex items-center gap-3 px-5 py-2" data-testid={`rate-book-item-${i}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-800 truncate">{r.service}</div>
        <div className="text-[11px] text-slate-400">per {r.unit || 'LS'}</div>
      </div>
      <div className="font-mono text-xs font-semibold text-slate-700 w-24 text-right">{fmt(r.rate)}</div>
      <Input
        type="number" min="0" step="0.01"
        value={qty[i] ?? ''} placeholder="qty"
        onChange={(e) => setQty((m) => ({ ...m, [i]: e.target.value }))}
        className="h-8 w-16 text-right font-mono" data-testid={`rate-book-qty-${i}`}
      />
      <Button size="sm" variant="outline" className="h-8" onClick={() => onAdd(r, qty[i])} data-testid={`rate-book-add-${i}`}>
        <Plus className="h-3.5 w-3.5 mr-1" />Add
      </Button>
    </div>
  );

  return (
    <Card className="p-0 overflow-hidden" data-testid="rate-book-picker">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-brand" /> Rate Book
          <span className="text-xs font-normal text-slate-400">from project Rate Card · {currency}</span>
        </div>
        {rates.length > 0 && (
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rates" className="h-8 pl-7 w-48 text-xs" data-testid="rate-book-search" />
          </div>
        )}
      </div>

      {rates.length === 0 ? (
        <div className="px-5 py-6 text-center text-xs text-slate-500">
          No rates on this project yet.{' '}
          {projectId && (
            <a href={`/projects/${projectId}`} className="text-brand hover:underline" data-testid="rate-book-project-link">
              Add or extract rates on the project page
            </a>
          )}
          {' '}to pick them here.
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
          {groups.map((g) => (
            <div key={g.category}>
              <div className="px-5 py-1.5 bg-slate-50/80 text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5" data-testid={`rate-book-group-${g.category}`}>
                <BookOpen className="h-3 w-3 text-brand" />{g.category}<span className="text-slate-400 font-normal">· {g.items.length}</span>
              </div>
              {g.items.map(({ r, i }) => <ItemRow key={r.id || i} r={r} i={i} />)}
            </div>
          ))}
          {filtered.length === 0 && <div className="px-5 py-4 text-center text-xs text-slate-400">No rates match “{q}”.</div>}
        </div>
      )}
      {rates.length > 0 && (
        <div className="px-5 py-2 border-t border-slate-200 bg-slate-50/60 text-[11px] text-slate-500">
          Picking a rate adds a Material line (quantity × rate). Rates are authored in the project currency ({currency}).
        </div>
      )}
    </Card>
  );
};
